
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import styles from '../core-exchange.module.scss';
export const metadata = {
  toc: true,
  title: 'User experience',
  section: 'User experience',
  layout: 'guide',
  description: 'Plaid user experience guide',
  preloadImages: [{
    src: '/assets/img/core-exchange/web-authn-example-flow.gif',
    type: 'image/gif'
  }],
  parentTocLevel: 2,
  childTocLevel: 3,
  secondLevelToc: true,
  alwaysExpand: true
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Image = makeShortcode("Image");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <div className={styles.page}>
      <Header title='User experience' subtitle={metadata.description} mdxType="Header" />
      <p>{`This page shows the different user experiences your customers will have once your institution is live on Core Exchange. No action is required. This is a visual reference to help you understand the end-user flow.`}</p>
      <p><a parentName="p" {...{
          "href": "/docs/link/"
        }}>{`Plaid Link`}</a>{` is the UI component users interact with to link their accounts to an app using Plaid.
Your institution will show up in Plaid Link after you go live on Core Exchange.`}</p>
      <h2 {...{
        "id": "institution-selection"
      }}>{`Institution selection`}</h2>
      <p>{`A user wants to connect their financial account to an app called WonderWallet.
WonderWallet sends them to Plaid Link where they can select their financial institution.
In this example, the financial institution is called First Platypus Bank.`}</p>
      <Image src='/assets/img/core-exchange/oauth-example-wonderwallet.png' alt='OAuth Example' mdxType="Image" />
      <h2 {...{
        "id": "data-transparency-messaging"
      }}>{`Data transparency messaging`}</h2>
      <p>{`Data Transparency Messaging (DTM) gives consumers a way of understanding the data types needed to power an app or service that they want to use.`}</p>
      <Image src='/assets/img/core-exchange/dtm.png' alt='Data transparency messaging example' width='75%' mdxType="Image" />
      <p>{`This enables Plaid and our customers to meet 1033 requirements:
the 1033 rule requires that Plaid and our customers (third parties) disclose the data types they are requesting access to, and for which use cases.
Additionally, Plaid customers need to ensure customer authorization is captured.
With DTM, Plaid shows data types and use cases on behalf of the customer in Link, and then captures the authorization.`}</p>
      <p>{`DTM will appear as a footer on the Pre-OAuth pane for all users that connect through OAuth.`}</p>
      <p>{`Plaid and Plaid customers will only access the specific data the user has consented to via DTM.
If additional data is required, the consumer will need to provide additional consent.`}</p>
      <p>{`When an item has been created with DTM, Plaid will also expose the consent records including the use case, data types, and time stamp of consent via Permissions Manager—this ensures you have full visibility into the consumers’ consent, enabling you (as a Data Provider) to “confirm” authorization under the rule.
If consent status or scope change on an item, such as if a user revokes access or grants additional access, these changes will also be reflected in the Permissions Manager records.`}</p>
      <p>{`To ensure a consistent user experience and to avoid repetition, Plaid recommends that you do not include a description of the data scopes/fields shared in your OAuth flow.`}</p>
      <h2 {...{
        "id": "authentication-guidance"
      }}>{`Authentication Guidance`}</h2>
      <h3 {...{
        "id": "oauth"
      }}>{`OAuth`}</h3>
      <p>{`The user is asked to authenticate with First Platypus.
Plaid recommends you do this using the OAuth 2.0 protocol.`}</p>
      <p>{`You own the OAuth user experience and host it on your domain.
We encourage you have two screens with your logo prominently featured: a login screen and two-step verification screen.`}</p>
      <Callout warning mdxType="Callout">
Your OAuth flow must implement <b>strong authentication</b> and not solely rely on username/password, incorporating multi-factor authentication with one-time passcodes or stronger methods like push-based authentication or biometrics through <a href="#optional-components">App2App or WebAuthn</a>.
      </Callout>
      <Image src='/assets/img/core-exchange/oauth-authentication.png' alt='OAuth Authentication Example Login Page' mdxType="Image" />
      <h2 {...{
        "id": "account-selection"
      }}>{`Account selection`}</h2>
      <p>{`If the user has more than one financial account, the user returns to Plaid Link and is asked to select which accounts they’d like to link.
The user then sees a success screen.
Tapping the continue button will return them to WonderWallet.`}</p>
      <Image src='/assets/img/core-exchange/oauth-example-account-selection.png' alt='Account Selection Example' mdxType="Image" />
      <h2 {...{
        "id": "returning-user-experience"
      }}>{`Returning user experience`}</h2>
      <p>{`Plaid’s returning user experience streamlines onboarding for users who have previously connected a financial account with Plaid via your institution’s OAuth and API integration.`}</p>
      <p>{`In `}<a parentName="p" {...{
          "href": "https://plaid.com/docs/link/"
        }}>{`Link`}</a>{`, a user can choose to be remembered by Plaid by entering their phone number and then verifying it using a one-time password.
Next, the user connects their account through your OAuth flow, which would be associated with their phone number.
When the same user wants to connect their account to another app, Plaid enables the user to do so using their phone number and secure authentication methods like one-time passcodes and passkeys.
This streamlined experience has built-in security controls and advanced monitoring that automatically detect suspicious activity and help prevent fraud.`}</p>
      <p>{`As a data partner for Core Exchange, you’re automatically enabled for this streamlined experience with no integration work required.
Your users can now opt-in and benefit from a faster way to securely connect their accounts to apps and services on the Plaid network.`}</p>
      <h2 {...{
        "id": "optional-components"
      }}>{`Optional components`}</h2>
      <h3 {...{
        "id": "app2app"
      }}>{`App2app`}</h3>
      <p><a parentName="p" {...{
          "href": "https://openid.net/tag/app2app/"
        }}>{`App2app`}</a>{` is a mechanism that allows mobile apps performing OAuth2 or OpenID Connect based authentication to offer a much simpler faster flow if the user already has an app provided by the authorization server owner installed on their mobile device.
The example below uses App2app.`}</p>
      <p>{`The user is brought to the First Platypus app to authenticate where they can use biometrics.`}</p>
      <Image src='/assets/img/core-exchange/app2app-example-flow.gif' alt='App2app Example Login Page' mdxType="Image" />
      <h3 {...{
        "id": "webauthn"
      }}>{`WebAuthn`}</h3>
      <p>{`We also recommend using `}<a parentName="p" {...{
          "href": "https://webauthn.guide/"
        }}>{`WebAuthn`}</a>{` for web-based authentication.
Users can enroll and then easily use biometrics to authenticate on any web browser.`}</p>
      <Image src='/assets/img/core-exchange/web-authn-example-flow.gif' alt='WebAuthn Biometric Login Example' mdxType="Image" />
    </div>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "institution-selection",
        level: 2,
        title: "Institution selection",
      },
{
        id: "data-transparency-messaging",
        level: 2,
        title: "Data transparency messaging",
      },
{
        id: "authentication-guidance",
        level: 2,
        title: "Authentication Guidance",
      },
{
        id: "oauth",
        level: 3,
        title: "OAuth",
      },
{
        id: "account-selection",
        level: 2,
        title: "Account selection",
      },
{
        id: "returning-user-experience",
        level: 2,
        title: "Returning user experience",
      },
{
        id: "optional-components",
        level: 2,
        title: "Optional components",
      },
{
        id: "app2app",
        level: 3,
        title: "App2app",
      },
{
        id: "webauthn",
        level: 3,
        title: "WebAuthn",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
